import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../AppIcon';
import Button from './Button';

const RoleBasedNavigation = ({ userRole = 'contractor' }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navigationConfig = {
    superadmin: [
      { path: '/homepage', label: 'Home', icon: 'Home', description: 'Terug naar homepage' },
      { path: '/super-admin-dashboard', label: 'Dashboard', icon: 'LayoutDashboard', description: 'Beheer overzicht' },
      { path: '/opdracht-plaatsen', label: 'Opdracht Plaatsen', icon: 'PlusCircle', description: 'Nieuwe opdracht aanmaken' },
      { path: '/opdracht-zoeken', label: 'Opdracht Zoeken', icon: 'Search', description: 'Alle opdrachten bekijken' },
      { path: '/wordt-partner', label: 'Wordt Partner', icon: 'Users', description: 'Partner beheer' },
      { path: '/homepage#about', label: 'Over Ons', icon: 'Info', description: 'Over BVM Group' },
      { path: '/user-profile-management', label: 'Profiel', icon: 'User', description: 'Account instellingen' }
    ],
    client: [
      { path: '/homepage', label: 'Home', icon: 'Home', description: 'Terug naar homepage' },
      { path: '/client-job-management', label: 'Dashboard', icon: 'LayoutDashboard', description: 'Mijn opdrachten' },
      { path: '/opdracht-plaatsen', label: 'Opdracht Plaatsen', icon: 'PlusCircle', description: 'Nieuwe opdracht plaatsen' },
      { path: '/opdracht-zoeken', label: 'Opdracht Zoeken', icon: 'Search', description: 'Opdrachten overzicht' },
      { path: '/homepage#about', label: 'Over Ons', icon: 'Info', description: 'Over BVM Group' },
      { path: '/user-profile-management', label: 'Profiel', icon: 'User', description: 'Account instellingen' }
    ],
    contractor: [
      { path: '/homepage', label: 'Home', icon: 'Home', description: 'Terug naar homepage' },
      { path: '/contractor-dashboard', label: 'Dashboard', icon: 'LayoutDashboard', description: 'Mijn werkzaamheden' },
      { path: '/opdracht-zoeken', label: 'Opdracht Zoeken', icon: 'Search', description: 'Beschikbare opdrachten' },
      { path: '/wordt-partner', label: 'Wordt Partner', icon: 'Users', description: 'Word affiliate partner' },
      { path: '/homepage#about', label: 'Over Ons', icon: 'Info', description: 'Over BVM Group' },
      { path: '/user-profile-management', label: 'Profiel', icon: 'User', description: 'Account instellingen' }
    ],
    affiliate: [
      { path: '/homepage', label: 'Home', icon: 'Home', description: 'Terug naar homepage' },
      { path: '/affiliate-dashboard', label: 'Dashboard', icon: 'LayoutDashboard', description: 'Partner overzicht' },
      { path: '/wordt-partner', label: 'Wordt Partner', icon: 'Users', description: 'Partner beheer' },
      { path: '/opdracht-zoeken', label: 'Opdracht Zoeken', icon: 'Search', description: 'Alle opdrachten' },
      { path: '/homepage#about', label: 'Over Ons', icon: 'Info', description: 'Over BVM Group' },
      { path: '/user-profile-management', label: 'Profiel', icon: 'User', description: 'Account instellingen' }
    ]
  };

  const currentNavigation = navigationConfig?.[userRole] || navigationConfig?.contractor;

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const navItemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.08,
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }),
    hover: {
      scale: 1.08,
      y: -2,
      transition: { 
        duration: 0.3,
        ease: 'easeOut'
      }
    },
    tap: {
      scale: 0.95,
      transition: { duration: 0.1 }
    }
  };

  const mobileMenuVariants = {
    hidden: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.2,
        when: 'afterChildren'
      }
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94],
        staggerChildren: 0.08,
        delayChildren: 0.1
      }
    }
  };

  const mobileItemVariants = {
    hidden: { opacity: 0, x: -30, scale: 0.9 },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: { 
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 h-16 z-[1000]" style={{ background: 'rgba(10,10,10,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #1A1A1A' }}>
        <div className="h-full px-4 lg:px-6 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/homepage" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <img src="/assets/images/Untitled-1774116510083.jpeg" alt="BVM Group Logo" className="h-10 w-auto object-contain" />
              <span className="font-heading font-bold text-lg text-white">BVM Group</span>
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {currentNavigation?.map((item, index) => (
                <motion.div
                  key={item?.path}
                  custom={index}
                  initial="hidden"
                  animate="visible"
                  whileHover="hover"
                  whileTap="tap"
                  variants={navItemVariants}
                >
                  <Link
                    to={item?.path}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-body transition-colors duration-200"
                    style={{
                      color: isActivePath(item?.path) ? '#00E5A0' : '#888888',
                      borderBottom: isActivePath(item?.path) ? '1px solid #00E5A0' : '1px solid transparent'
                    }}
                    title={item?.description}
                  >
                    <Icon name={item?.icon} size={16} color={isActivePath(item?.path) ? '#00E5A0' : '#888888'} />
                    <span>{item?.label}</span>
                  </Link>
                </motion.div>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMobileMenu}
              className="lg:hidden text-white"
              aria-label="Toggle menu"
            >
              <motion.div animate={{ rotate: isMobileMenuOpen ? 90 : 0 }} transition={{ duration: 0.3 }}>
                <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={22} color="#888888" />
              </motion.div>
            </Button>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="fixed inset-0 z-[1020] lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="absolute inset-0"
              style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
              onClick={toggleMobileMenu}
            />
            <motion.nav
              className="absolute top-16 left-0 right-0 py-4"
              style={{ background: '#0A0A0A', borderBottom: '1px solid #1A1A1A' }}
              variants={mobileMenuVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <div className="px-4 space-y-1">
                {currentNavigation?.map((item, index) => (
                  <motion.div key={item?.path} variants={mobileItemVariants} custom={index}>
                    <Link
                      to={item?.path}
                      onClick={toggleMobileMenu}
                      className="flex items-center gap-3 px-4 py-3 text-sm font-body transition-colors duration-200"
                      style={{ color: isActivePath(item?.path) ? '#00E5A0' : '#888888', borderBottom: '1px solid #1A1A1A' }}
                    >
                      <Icon name={item?.icon} size={16} color={isActivePath(item?.path) ? '#00E5A0' : '#888888'} />
                      <span>{item?.label}</span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default RoleBasedNavigation;